import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { discussionService, Post, PostQueryParams } from '../../api/discussionService';
import PostList from './PostList';
import CreatePostForm from './CreatePostForm';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const CommunityContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
`;

const FilterContainer = styled.div`
  display: flex;
  margin-bottom: 20px;
  gap: 10px;
`;

const FilterButton = styled.button<{ active: boolean }>`
  background-color: ${props => props.active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.active ? '#fff' : props.theme.colors.text};
  border: 1px solid ${props => props.theme.colors.primary};
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.active ? props.theme.colors.primaryDark : props.theme.colors.bgLight};
  }
`;

const CommunityPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [queryParams, setQueryParams] = useState<PostQueryParams>({
    limit: 10,
    offset: 0,
    status: 'published'
  });
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  
  // Fetch posts using React Query v5
  const { 
    data, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['posts', queryParams],
    queryFn: () => discussionService.getPosts(queryParams)
  });
  
  const handleCategoryFilter = (category: string | null) => {
    setActiveCategory(category);
    setQueryParams({
      ...queryParams,
      category: category || undefined,
      offset: 0 // Reset pagination when changing filters
    });
  };
  
  const handleCreateSuccess = () => {
    refetch();
    setShowCreateForm(false);
  };

  const handleCreatePostClick = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/community' } });
      return;
    }
    setShowCreateForm(!showCreateForm);
  };
  
  const categories = ['General', 'Questions', 'Testimonials', 'Resources'];
  
  // Extract data from the response
  const responseData = data?.data;
  const posts = responseData?.posts || [];
  const pagination = responseData?.pagination || { limit: 10, offset: 0, total: 0 };
  
  return (
    <CommunityContainer>
      <Header>
        <Title>Community</Title>
        <FilterButton 
          active={showCreateForm} 
          onClick={handleCreatePostClick}
        >
          {showCreateForm ? 'Cancel' : 'Create Post'}
        </FilterButton>
      </Header>
      
      {showCreateForm && (
        <CreatePostForm onSuccess={handleCreateSuccess} onCancel={() => setShowCreateForm(false)} />
      )}
      
      <FilterContainer>
        <FilterButton 
          active={activeCategory === null} 
          onClick={() => handleCategoryFilter(null)}
        >
          All
        </FilterButton>
        {categories.map(category => (
          <FilterButton 
            key={category}
            active={activeCategory === category.toLowerCase()} 
            onClick={() => handleCategoryFilter(category.toLowerCase())}
          >
            {category}
          </FilterButton>
        ))}
      </FilterContainer>
      
      {isLoading ? (
        <p>Loading posts...</p>
      ) : error ? (
        <p>Error loading posts. Please try again later.</p>
      ) : posts.length > 0 ? (
        <PostList 
          posts={posts} 
          pagination={pagination}
          onPageChange={(newOffset: number) => setQueryParams({...queryParams, offset: newOffset})}
        />
      ) : (
        <p>No posts found. Be the first to start a discussion!</p>
      )}
    </CommunityContainer>
  );
};

export default CommunityPage; 